import { AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios';
import { endOfDay } from 'date-fns/endOfDay';
import { startOfDay } from 'date-fns/startOfDay';
import { subDays } from 'date-fns/subDays';
import { format } from 'date-fns-tz';

import { TOKEN_INSTANCES } from 'api/base';
import { PORTAL_USERS_KEYS } from 'api/portalUser/constants';
import { TIMEZONE_MASK } from 'constants/common';
import { queryClient as appQueryClient } from 'queries';
import { notificationService } from 'utils/notificationService';

import { LAST_ACTIVITY_STORAGE_KEY, SESSION_CONFIG, SESSION_KEYS } from './constants';

export const updateRequestHeaders = (value?: string): void => {
  const authorization = value ? `Bearer ${value}` : '';

  TOKEN_INSTANCES.forEach((instance) => {
    instance.defaults.headers.common.Authorization = authorization;
  });
};

export const updateConfigHeader = (
  config: InternalAxiosRequestConfig,
  token: string,
): InternalAxiosRequestConfig => {
  const headers = {
    ...config.headers,
    Authorization: `Bearer ${token}`,
  } as AxiosRequestHeaders;

  return {
    ...config,
    headers,
  };
};

export const logoutWithQuery = (queryClient: typeof appQueryClient): void => {
  queryClient.cancelQueries();
  queryClient.removeQueries({
    predicate: (query) =>
      !query.queryKey.includes(SESSION_KEYS.IS_INITIALIZED) &&
      !query.queryKey.includes(SESSION_KEYS.IS_THEME_REQUESTED) &&
      !query.queryKey.includes(PORTAL_USERS_KEYS.ORGANIZATION_SETTINGS),
  });
  queryClient.setQueryData([SESSION_KEYS.SESSION], null);
  queryClient.setQueryData([SESSION_KEYS.IS_LOGGED_IN], false);
  notificationService.removeAll();
};

export const getTransactionReportingPeriod = (timeZone = 'Etc/UTC', days = 3) => {
  const now = new Date();
  const currDate = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const startDate = format(subDays(startOfDay(currDate), days - 1), TIMEZONE_MASK, {
    timeZone,
  });
  const endDate = format(endOfDay(currDate), TIMEZONE_MASK, {
    timeZone,
  });

  return { endDate, startDate };
};

// Per-tab fallback for when localStorage writes fail
let memoryLastActivity = 0;
let isIdleLatched = false;

export const getLastActivity = (): number => {
  try {
    return Math.max(
      memoryLastActivity,
      Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) || 0,
    );
  } catch {
    return memoryLastActivity;
  }
};

const writeLastActivity = (now: number): void => {
  memoryLastActivity = now;

  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable: this tab falls back to memoryLastActivity
  }
};

export const markActivity = (): void => {
  const now = Date.now();

  if (isIdleLatched || now - getLastActivity() < SESSION_CONFIG.ACTIVITY_THROTTLE) {
    return;
  }

  writeLastActivity(now);
};

export const resetLastActivity = (): void => {
  isIdleLatched = false;
  writeLastActivity(Date.now());
};

// Latches until the next login so activity during the logout request can't revive the session
export const isIdleExpired = (): boolean => {
  const lastActivity = getLastActivity();

  if (lastActivity && Date.now() - lastActivity >= SESSION_CONFIG.IDLE_TIMEOUT) {
    isIdleLatched = true;
  }

  return isIdleLatched;
};
